import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaLink, FaSpinner, FaExternalLinkAlt, FaCheck } from 'react-icons/fa';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { fetchToken } from '../../../lib/globalApiClient';
import './productDetail.css';

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';
const GLOBAL_API = process.env.REACT_APP_APIUrl1688;

const SellerProductDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const message = useMessage();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [mappingSkuId, setMappingSkuId] = useState(null);
  const [pricingCache, setPricingCache] = useState({});

  const getVariantSources = (result) => {
    if (!result) return [];
    return [
      ...(Array.isArray(result.variations) ? result.variations : []),
      ...(Array.isArray(result.productSkuInfos) ? result.productSkuInfos : []),
      ...(Array.isArray(result.skuInfos) ? result.skuInfos : []),
      ...(Array.isArray(result.skuList) ? result.skuList : []),
      ...(Array.isArray(result.skus) ? result.skus : []),
      ...(Array.isArray(result.productInfo?.skuInfos) ? result.productInfo.skuInfos : []),
      ...(Array.isArray(result.data?.productSkuInfos) ? result.data.productSkuInfos : []),
      ...(Array.isArray(result.variants) ? result.variants : []),
    ];
  };

  const normalizeVariantAttributes = (sku) => {
    if (!sku) return [];

    if (Array.isArray(sku.skuAttributes) && sku.skuAttributes.length > 0) {
      return sku.skuAttributes.map((attr) => ({
        name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
        value: attr.valueTrans || attr.value || attr.attributeValue || '',
        image: attr.skuImageUrl || attr.imageUrl || null,
      }));
    }

    if (Array.isArray(sku.attributes) && sku.attributes.length > 0) {
      return sku.attributes.map((attr) => ({
        name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
        value: attr.valueTrans || attr.value || attr.attributeValue || '',
        image: attr.skuImageUrl || attr.imageUrl || null,
      }));
    }

    if (Array.isArray(sku.specAttrs) && sku.specAttrs.length > 0) {
      return sku.specAttrs.map((attr) => ({
        name: attr.name || attr.attributeName || 'Attribute',
        value: attr.value || attr.attributeValue || '',
        image: attr.imageUrl || null,
      }));
    }

    if (sku.specAttrsText) {
      return [{ name: 'Variant', value: sku.specAttrsText, image: null }];
    }

    return [];
  };

  // Get params from URL or state
  const offerIdFromQuery = searchParams.get('offerId') || '';
  const asinFromQuery = searchParams.get('asin') || '';
  const productFromState = location.state?.product;
  const amazonProductFromState = location.state?.amazonProduct;

  // Calculate INR price using pricing API
  const calculateINRPrice = useCallback(async (rmbPrice, offerId = '') => {
    if (!rmbPrice || rmbPrice <= 0) return 0;

    const cacheKey = rmbPrice.toFixed(2);
    if (pricingCache[cacheKey]) {
      return pricingCache[cacheKey];
    }

    try {
      const calculationData = {
        ap: parseFloat(rmbPrice),
        ls: 0,
        hsn: false,
        hsnTaxPercent: 18,
        dw: 0.5,
        vw: 0.5 / 5000,
        sku: String(offerId || ''),
      };

      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(calculationData)
      });

      const data = await response.json();

      if (data?.data?.breakdown?.FBP) {
        const inrPrice = parseFloat(data.data.breakdown.FBP);
        setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
        return inrPrice;
      }
      return 0;
    } catch (error) {
      console.error('Pricing calculation failed:', error);
      return 0;
    }
  }, [pricingCache]);

  // Fetch product details from 1688 API (same as frontend)
  const fetchProductDetails = async (offerId) => {
    setLoading(true);
    try {
      console.log('[SellerProductDetail] Fetching product:', offerId);

      const token = await fetchToken();
      const response = await fetch(`${GLOBAL_API}/product-detail-by-offerid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: offerId, country: 'en' })
      });

      const data = await response.json();
      console.log('[SellerProductDetail] API Response:', data);

      // Check for error response
      if (data.result && data.result.success === false) {
        const errorMessage = data.result.message || 'Product not found';
        message.showToast(errorMessage, ToastTypes.ERROR);
        setLoading(false);
        return;
      }

      const result = data.result?.result;
      if (!result) {
        message.showToast('Product data not found', ToastTypes.ERROR);
        setLoading(false);
        return;
      }

      // Get base price from priceRangeList or priceInfo
      const priceRangeList = result.productSaleInfo?.priceRangeList || [];
      const basePrice = parseFloat(
        priceRangeList[0]?.price ||
        result.priceInfo?.price ||
        0
      );
      const inrPrice = await calculateINRPrice(basePrice, offerId);
      const images = result.productImage?.images || [];

      // Process variants with pricing from variations + fallback SKU sources.
      const variantSources = getVariantSources(result);
      const variants = [];

      for (const [idx, sku] of variantSources.entries()) {
        const variantPrice = parseFloat(
          sku?.price ||
          sku?.consignPrice ||
          sku?.salePrice ||
          sku?.retailPrice ||
          sku?.priceInfo?.price ||
          basePrice ||
          0
        );

        const variantInrPrice = await calculateINRPrice(variantPrice, offerId);
        const attributes = normalizeVariantAttributes(sku);
        const variantImage =
          attributes.find((attr) => attr.image)?.image ||
          sku?.skuImage ||
          sku?.image ||
          sku?.imageUrl ||
          sku?.pic ||
          sku?.picUrl ||
          images[0] ||
          '';

        const skuId = sku?.skuId || sku?.id || sku?.specId || sku?.sku || `sku-${idx}`;

        variants.push({
          skuId,
          price: Number.isFinite(variantPrice) ? variantPrice : 0,
          inrPrice: Number.isFinite(variantInrPrice) ? variantInrPrice : 0,
          consignPrice: sku?.consignPrice,
          amountOnSale: sku?.amountOnSale || sku?.stock || sku?.canBookCount || sku?.quantity || 0,
          image: variantImage,
          attributes,
        });
      }

      const uniqueVariants = Array.from(
        new Map(variants.map((variant) => [variant.skuId, variant])).values()
      );

      setProduct({
        offerId: offerId,
        title: result.subjectTrans || result.subject || 'Product',
        description: result.description || '',
        categoryName: result.categoryName || result.category || '',
        unit: result.productSaleInfo?.unit || result.unit || 'piece',
        companyName: result.companyName || result.sellerInfo?.companyName || '',
        images: images,
        price: {
          rmb: basePrice,
          inr: inrPrice
        },
        priceRange: priceRangeList,
        variants: uniqueVariants,
        minOrderQuantity: result.minOrderQuantity || 1,
        supplier: result.productShippingInfo?.sendGoodsAddressText || result.companyName || 'China',
        monthSold: result.monthSold || 0,
      });

      if (uniqueVariants.length > 0) {
        setSelectedVariant(uniqueVariants[0]);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      message.showToast('Failed to load product details', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Map ASIN to product/variant
  const handleMapAsin = async (skuId = null) => {
    const asin = asinFromQuery || amazonProductFromState?.asin;
    if (!asin || !product?.offerId) {
      message.showToast('Missing ASIN or Product ID', ToastTypes.ERROR);
      return;
    }

    setMappingSkuId(skuId || 'main');

    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      if (!token) {
        message.showToast('Please login to map products', ToastTypes.ERROR);
        return;
      }

      const response = await fetch(`${BACKEND_API}order/map-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          asin: asin,
          offerId: product.offerId.toString(),
          skuId: skuId || null,
          productTitle: product.title,
          productImage: product.images?.[0] || '',
          productPrice: product.price.rmb,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const mappedTo = skuId ? `SKU ${skuId}` : `product ${product.offerId}`;
        message.showToast(`Successfully mapped ASIN ${asin} to ${mappedTo}`, ToastTypes.SUCCESS);
        setTimeout(() => navigate(-1), 1500);
      } else {
        message.showToast(data.message || 'Failed to map product', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Map failed:', error);
      message.showToast('Failed to map product', ToastTypes.ERROR);
    } finally {
      setMappingSkuId(null);
    }
  };

  // Initialize
  useEffect(() => {
    const offerId = offerIdFromQuery || productFromState?.offerId;
    if (offerId) {
      fetchProductDetails(offerId);
    } else {
      setLoading(false);
      message.showToast('No product ID provided', ToastTypes.ERROR);
    }
  }, [offerIdFromQuery]);

  // Handle image error
  const handleImageError = (e) => {
    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f0f0f0" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="14">No Image</text></svg>';
  };

  if (loading) {
    return (
      <div className="spd-container">
        <div className="spd-loading">
          <FaSpinner className="spd-spinner" />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="spd-container">
        <div className="spd-error">
          <p>Product not found</p>
          <button className="spd-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="spd-container">
      {/* Header */}
      <div className="spd-header">
        <button className="spd-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="spd-title">Product Detail</h2>
        {asinFromQuery && (
          <span className="spd-asin-badge">Mapping to ASIN: {asinFromQuery}</span>
        )}
      </div>

      <div className="spd-content">
        {/* Left - Images */}
        <div className="spd-left">
          <div className="spd-main-image">
            <img
              src={product.images[selectedImage] || product.images[0]}
              alt={product.title}
              referrerPolicy="no-referrer"
              onError={handleImageError}
            />
          </div>
          <div className="spd-thumbnails">
            {product.images.slice(0, 6).map((img, idx) => (
              <div
                key={idx}
                className={`spd-thumb ${selectedImage === idx ? 'active' : ''}`}
                onClick={() => setSelectedImage(idx)}
              >
                <img src={img} alt="" referrerPolicy="no-referrer" onError={handleImageError} />
              </div>
            ))}
          </div>
        </div>

        {/* Right - Details */}
        <div className="spd-right">
          <h1 className="spd-product-title">{product.title}</h1>

          <div className="spd-meta">
            <span className="spd-offer-id">Offer ID: {product.offerId}</span>
            <span className="spd-supplier">📍 {product.supplier}</span>
            {product.monthSold > 0 && (
              <span className="spd-sold">{product.monthSold} sold/month</span>
            )}
          </div>

          {/* Price */}
          <div className="spd-price-box">
            <div className="spd-price-row">
              <span className="spd-price-label">🇨🇳 RMB Price:</span>
              <span className="spd-price-rmb">¥{product.price.rmb.toFixed(2)}</span>
            </div>
            <div className="spd-price-row">
              <span className="spd-price-label">🇮🇳 INR Price:</span>
              <span className="spd-price-inr">₹{product.price.inr.toFixed(2)}</span>
            </div>
            <div className="spd-moq">MOQ: {product.minOrderQuantity} pcs</div>
          </div>

          {/* Price Range */}
          {product.priceRange && product.priceRange.length > 1 && (
            <div className="spd-price-range">
              <h4>Price Tiers</h4>
              <div className="spd-tiers">
                {product.priceRange.map((range, idx) => (
                  <div key={idx} className="spd-tier">
                    <span className="spd-tier-qty">{range.startQuantity}+ pcs</span>
                    <span className="spd-tier-price">¥{parseFloat(range.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Details */}
          <div style={{ marginTop: '14px', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1f2937' }}>Product Details</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
              <div><strong>Company:</strong> {product.companyName || 'N/A'}</div>
              <div><strong>Category:</strong> {product.categoryName || 'N/A'}</div>
              <div><strong>Unit:</strong> {product.unit || 'piece'}</div>
              <div><strong>Variants:</strong> {product.variants?.length || 0}</div>
            </div>
            {product.description && (
              <p style={{ margin: '10px 0 0 0', color: '#4b5563', fontSize: '12px', lineHeight: 1.5 }}>
                {product.description.slice(0, 280)}{product.description.length > 280 ? '...' : ''}
              </p>
            )}
          </div>

          {/* External Link */}
          <button
            className="spd-external-btn"
            onClick={() => window.open(`https://detail.1688.com/offer/${product.offerId}.html`, '_blank')}
          >
            <FaExternalLinkAlt /> View on 1688
          </button>
        </div>
      </div>

      {/* Variants Section */}
      {product.variants && product.variants.length > 0 && (
        <div className="spd-variants-section">
          <h3 className="spd-variants-title">
            Product Variants ({product.variants.length})
            {asinFromQuery && <span className="spd-variants-hint">Click "Map" to link a variant SKU to ASIN</span>}
          </h3>

          <div className="spd-variants-grid">
            {product.variants.map((variant, idx) => (
              <div
                key={variant.skuId || idx}
                className={`spd-variant-card ${selectedVariant?.skuId === variant.skuId ? 'selected' : ''}`}
                onClick={() => setSelectedVariant(variant)}
              >
                {/* Variant Image */}
                {variant.image && (
                  <div className="spd-variant-image">
                    <img
                      src={variant.image}
                      alt=""
                      referrerPolicy="no-referrer"
                      onError={handleImageError}
                    />
                  </div>
                )}

                <div className="spd-variant-info">
                  <div className="spd-variant-sku">SKU: {variant.skuId}</div>

                  {/* Attributes */}
                  <div className="spd-variant-attrs">
                    {variant.attributes.length > 0 ? (
                      variant.attributes.map((attr, i) => (
                        <span key={i} className="spd-variant-attr">
                          {attr.name}: {attr.value}
                        </span>
                      ))
                    ) : (
                      <span className="spd-variant-attr">No attributes available</span>
                    )}
                  </div>

                  {/* Prices */}
                  <div className="spd-variant-prices">
                    <span className="spd-variant-rmb">¥{variant.price.toFixed(2)}</span>
                    <span className="spd-variant-inr">₹{variant.inrPrice.toFixed(2)}</span>
                  </div>

                  {variant.amountOnSale > 0 && (
                    <div className="spd-variant-stock">Stock: {variant.amountOnSale}</div>
                  )}
                </div>

                {/* Map Button */}
                {asinFromQuery && (
                  <button
                    className="spd-variant-map-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMapAsin(variant.skuId);
                    }}
                    disabled={mappingSkuId === variant.skuId}
                  >
                    {mappingSkuId === variant.skuId ? (
                      <FaSpinner className="spd-spinner" />
                    ) : (
                      <><FaLink /> Map</>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerProductDetail;
